import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext.js';
import Navigation from '../components/Navigation.js';
import { LAYOUT_CONFIG } from '../constants/layout.js';

const useAutoResizeTextarea = () => {
  const textareaRef = useRef(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.max(textarea.scrollHeight, 56);
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, []);

  return { textareaRef, adjustHeight };
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  
  const [globalPolishInstruction, setGlobalPolishInstruction] = useState('');
  const [userHotwords, setUserHotwords] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const globalInstructionRef = useAutoResizeTextarea();
  const userHotwordsRef = useAutoResizeTextarea();

  useEffect(() => {
    loadUserSettings();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      globalInstructionRef.adjustHeight();
      userHotwordsRef.adjustHeight();
    }, 0);
  }, [globalPolishInstruction, userHotwords]);

  const loadUserSettings = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getUserSettings();

      if (response.success) {
        const settings = response.data;
        setGlobalPolishInstruction(settings.globalPolishInstruction || '');
        setUserHotwords(settings.userHotwords || '');
      }
    } catch (error) {
      console.error('获取用户设置失败:', error);
      showMessage('获取设置失败，请刷新页面重试', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleGlobalInstructionChange = (e) => {
    setGlobalPolishInstruction(e.target.value);
    globalInstructionRef.adjustHeight();
  };

  const handleUserHotwordsChange = (e) => {
    setUserHotwords(e.target.value);
    userHotwordsRef.adjustHeight();
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      const settings = {
        globalPolishInstruction: globalPolishInstruction.trim(),
        userHotwords: userHotwords.trim()
      };

      console.log('🔧 开始保存设置:', settings);
      console.log('🔍 当前token:', localStorage.getItem('authToken')?.substring(0, 20) + '...');

      const response = await apiService.saveUserSettings(settings);

      console.log('📋 保存设置响应:', response);

      if (response.success) {
        showMessage('设置保存成功');
        console.log('✅ 设置保存成功');
      } else {
        showMessage('保存失败：' + (response.error || '未知错误'), 'error');
        console.error('❌ 设置保存失败:', response.error);
      }
    } catch (error) {
      console.error('保存设置失败:', error);
      showMessage('保存失败，请重试', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (window.confirm('确定要重置润色指令为默认值吗？用户热词将保留不变。')) {
      try {
        const response = await apiService.getDefaultSettings();

        if (response.success) {
          const settings = response.data;
          setGlobalPolishInstruction(settings.globalPolishInstruction || '');
          setUserHotwords(userHotwords);
          showMessage('润色指令已重置为默认值，用户热词保持不变');
        } else {
          showMessage('获取默认设置失败', 'error');
        }
      } catch (error) {
        console.error('获取默认设置失败:', error);
        showMessage('获取默认设置失败，请重试', 'error');
      }
    }
  };

  const handleLogout = async () => {
    if (window.confirm('确定要登出吗？')) {
      await logout();
      navigate('/login');
    }
  };

  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-bg text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue mx-auto mb-4"></div>
          <p>加载设置中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg text-white pb-20 flex items-center justify-center">
      <div className={`w-full ${LAYOUT_CONFIG.SETTINGS_MAX_WIDTH} ${LAYOUT_CONFIG.PAGE_PADDING} ${LAYOUT_CONFIG.PAGE_SPACING}`}>

        {/* 用户信息区域 */}
        <div className="bg-gray-900 rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-4">
            {user?.avatarUrl && (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            )}
            {!user?.avatarUrl && (
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <div className="text-lg font-medium text-white">
                {user?.name || '用户'}
              </div>
              <div className="text-sm text-gray-400">
                {user?.email || '未设置邮箱'}
              </div>
            </div>
          </div>
        </div>

        {/* 消息提示 */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg text-center ${
            message.includes('失败') || message.includes('错误')
              ? 'bg-red-900/50 text-red-200'
              : 'bg-green-900/50 text-green-200'
          }`}>
            {message}
          </div>
        )}

        {/* 全局润色指令 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            全局润色指令
          </label>
          <textarea
            ref={globalInstructionRef.textareaRef}
            value={globalPolishInstruction}
            onChange={handleGlobalInstructionChange}
            className="w-full bg-gray-900 rounded-lg p-4 text-white text-sm resize-none focus:ring-2 focus:ring-primary-blue focus:outline-none overflow-hidden"
            style={{ minHeight: '56px' }}
            placeholder=""
            spellCheck={false}
          />
        </div>

        {/* 用户热词 */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            用户热词
          </label>
          <textarea
            ref={userHotwordsRef.textareaRef}
            value={userHotwords}
            onChange={handleUserHotwordsChange}
            className="w-full bg-gray-900 rounded-lg p-4 text-white text-sm resize-none focus:ring-2 focus:ring-primary-blue focus:outline-none overflow-hidden"
            style={{ minHeight: '56px' }}
            placeholder=""
            spellCheck={false}
          />
        </div>

        {/* 操作按钮 */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={saveSettings}
            disabled={isSaving}
            className={`flex-1 py-3 px-6 rounded-full font-medium transition-colors ${
              isSaving
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSaving ? '保存中...' : '保存配置'}
          </button>

          <button
            onClick={resetToDefault}
            className="flex-1 py-3 px-6 bg-gray-600 text-white rounded-full font-medium hover:bg-gray-700 transition-colors"
          >
            重置润色指令
          </button>
        </div>

        {/* 登出按钮 */}
        <button
          onClick={handleLogout}
          className="w-full py-3 px-6 bg-gray-700 text-white rounded-full font-medium hover:bg-gray-600 transition-colors"
        >
          登出
        </button>
      </div>

      <Navigation />
    </div>
  );
};

export default SettingsPage;